import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Organisation, TaskPlan, User as TPUser } from "./types";
import {
  deletePlan,
  deleteRun,
  getPlanById,
  startRun,
} from "./services/storageService";
import { calculatePlanDuration } from "./services/schedulingService";
import PlanEditor from "./components/PlanEditor";
import PlanRunner from "./components/PlanRunner";
import TeamManager from "./components/TeamManager";
import GithubCallback from "./components/GithubCallback";
import ShareModal from "./components/ShareModal";
import Landing from "./components/Landing";
import Pricing from "./components/Pricing";
import ComingSoon from "./components/ComingSoon";
import Dashboard from "./components/Dashboard";
import RunsList from "./components/RunsList";
import SubscriptionSuccess from "./components/SubscriptionSuccess";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import { UserSettings } from "@/components/UserSettings.tsx";
import { NotificationsProvider } from "@/components/NotificationsProvider.tsx";
import {
  useWorkspace,
  WorkspaceProvider,
} from "@/components/WorkspaceProvider.tsx";
import { useWorkspaceData } from "@/components/useWorkspaceData.ts";
import {
  ConfirmationModal,
  ErrorModal,
  LimitReachedModal,
} from "./components/Modals";
import { Clock, Menu, XCircle } from "./components/Icons";
import { SideNav } from "@/components/SideNav.tsx";
import { CreateOrgModal } from "@/components/CreateOrgModal.tsx";
import { formatFullDate } from "./shared/date";

interface RunModalProps {
  plan: TaskPlan;
  onClose: () => void;
  onConfirm: (name: string, schedule: string) => void;
}

const RunModal: React.FC<RunModalProps> = ({ plan, onClose, onConfirm }) => {
  const { getToken } = useAuth();
  const [fullPlan, setFullPlan] = useState<TaskPlan | null>(null);

  const [name, setName] = useState(plan.name);
  const [schedule, setSchedule] = useState(
    plan.estimatedStartTime
      ? new Date(plan.estimatedStartTime).toISOString().slice(0, 16)
      : "",
  );
  const [mode, setMode] = useState<"NOW" | "LATER">(
    plan.estimatedStartTime ? "LATER" : "NOW",
  );
  const [isNameModified, setIsNameModified] = useState(false);

  useEffect(() => {
    if (!plan.tasks || plan.tasks.length === 0) {
      const fetchPlan = async () => {
        try {
          const token = await getToken();
          if (token) {
            const p = await getPlanById(plan.id, token);
            setFullPlan(p);
          }
        } catch (e) {
          console.error("Failed to fetch plan details for duration", e);
        }
      };
      fetchPlan();
    }
  }, [plan, getToken]);

  useEffect(() => {
    if (!isNameModified) {
      setName(plan.name);
    }
  }, [schedule, plan.name, isNameModified]);

  const runStartTime = useMemo(() => {
    const start =
      mode === "LATER" && schedule ? new Date(schedule).getTime() : Date.now();
    return isNaN(start) ? Date.now() : start;
  }, [mode, schedule]);

  const planDurationMinutes = useMemo(() => {
    const tasksToUse = fullPlan?.tasks || plan.tasks || [];
    return calculatePlanDuration(tasksToUse, runStartTime);
  }, [plan.tasks, fullPlan, runStartTime]);

  const estimatedEndTime = useMemo(() => {
    return new Date(runStartTime + planDurationMinutes * 60 * 1000);
  }, [runStartTime, planDurationMinutes]);

  return (
    <div className="fixed inset-0 bg-black/60 z-modal flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-800">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50 rounded-t-2xl">
          <h3 className="font-bold text-lg text-slate-800 dark:text-white">
            Run "{plan.name}"
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
          >
            <XCircle size={20} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 dark:text-slate-400 mb-1.5">
              Execution Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setIsNameModified(true);
              }}
              className="w-full border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2.5 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 dark:text-slate-400 mb-2">
              When to run?
            </label>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <button
                onClick={() => {
                  setMode("NOW");
                  setSchedule("");
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${mode === "NOW" ? "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-500/20" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600"}`}
              >
                Start Now
              </button>
              <button
                onClick={() => setMode("LATER")}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${mode === "LATER" ? "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-500/20" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600"}`}
              >
                Schedule for Later
              </button>
            </div>

            {mode === "LATER" && (
              <div className="animate-in fade-in slide-in-from-top-2">
                <input
                  type="datetime-local"
                  value={schedule}
                  onChange={(e) => setSchedule(e.target.value)}
                  className="w-full border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-2.5 text-sm"
                />
              </div>
            )}

            {estimatedEndTime && (
              <div className="mt-3 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 p-3 rounded-lg border border-slate-100 dark:border-slate-800 flex items-center gap-2">
                <Clock size={14} className="text-indigo-500" />
                <span>
                  Estimated completion:{" "}
                  <span className="font-bold text-slate-700 dark:text-slate-300">
                    {formatFullDate(estimatedEndTime)}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(name, schedule)}
            disabled={mode === "LATER" && !schedule}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm shadow-lg shadow-indigo-500/20 transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mode === "LATER" ? "Schedule Run" : "Start Execution"}
          </button>
        </div>
      </div>
    </div>
  );
};

interface AppShellProps {
  children: React.ReactNode;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  currentOrg: Organisation;
  organisations: Organisation[];
  onSwitchOrg: (org: Organisation) => void;
  onOrgCreated: (org: Organisation) => void;
  tpUser: TPUser;
  modals: React.ReactNode;
  onOpenCreateOrg: () => void;
}

const AppShell: React.FC<AppShellProps> = ({
  children,
  mobileMenuOpen,
  setMobileMenuOpen,
  currentOrg,
  organisations,
  onSwitchOrg,
  onOrgCreated,
  tpUser,
  modals,
  onOpenCreateOrg,
}) => {
  const location = useLocation();

  // Helper to check active route - matches the primary segment after the slug
  const isActive = (path: string) => {
    const parts = location.pathname.split("/").filter(Boolean);
    // parts[0] is the slug, parts[1] is the main section
    const segment = path.startsWith("/") ? path.substring(1) : path;
    return parts[1] === segment;
  };

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden font-sans text-slate-900 dark:text-slate-100">
      {modals}

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-drawer-backdrop md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}

      <SideNav
        currentOrg={currentOrg}
        organisations={organisations}
        onSwitchOrg={onSwitchOrg}
        onOrgCreated={onOrgCreated}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        isActive={isActive}
        tpUser={tpUser}
        onOpenCreateOrg={onOpenCreateOrg}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-950">
        <div className="md:hidden flex items-center p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="text-slate-500"
          >
            <Menu />
          </button>
          <span className="ml-4 font-bold text-lg dark:text-white">
            {isActive("/dashboard") ? "Dashboard" : "Workspace"}
          </span>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

const WorkspaceRoutes: React.FC = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const {
    organisations,
    currentOrg,
    switchOrg,
    refreshOrgs,
    isLoading: isOrgsLoading,
  } = useWorkspace();

  const location = useLocation();
  const {
    plans,
    runs,
    isLoading: isDataLoading,
    refresh: refreshData,
  } = useWorkspaceData({ autoFetch: false });

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Create Org Modal
  const [createOrgModalOpen, setCreateOrgModalOpen] = useState(false);

  // Modal States
  const [runModalPlan, setRunModalPlan] = useState<TaskPlan | null>(null);
  const [shareModalPlan, setShareModalPlan] = useState<TaskPlan | null>(null);
  const [deleteConfirmPlanId, setDeleteConfirmPlanId] = useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [limitReachedMessage, setLimitReachedMessage] = useState<string | null>(
    null,
  );

  // Map Clerk user to our TPUser type
  const tpUser: TPUser | null = user
    ? {
        id: user.id,
        name: user.fullName || "User",
        email: user.primaryEmailAddress?.emailAddress || "",
        initials: user.firstName?.[0] || "U",
        organisationIds: [],
      }
    : null;

  // Sync Org with URL Slug
  useEffect(() => {
    if (isOrgsLoading) return;

    if (slug) {
      const foundOrg = organisations.find((o) => o.slug === slug);
      if (foundOrg) {
        if (currentOrg?.id !== foundOrg.id) {
          switchOrg(slug);
        }
      } else if (organisations.length > 0) {
        // Slug in URL is invalid, redirect to first org
        navigate(`/${organisations[0].slug}/dashboard`, { replace: true });
      }
    } else if (organisations.length > 0) {
      // No Slug, redirect to first org or last visited
      const lastVisited = localStorage.getItem("lastVisitedOrgSlug");
      const target =
        organisations.find((o) => o.slug === lastVisited) || organisations[0];
      navigate(`/${target.slug}/dashboard`, { replace: true });
    }
  }, [slug, organisations, isOrgsLoading, navigate]);

  // Sync Data with Route
  useEffect(() => {
    if (currentOrg && currentOrg.slug === slug) {
      const isDashboard = location.pathname.endsWith("/dashboard");
      const isRunsList = location.pathname.endsWith("/runs");

      if (isDashboard || isRunsList) {
        refreshData({
          dashboard: isDashboard,
          runsOnly: isRunsList && !isDashboard,
          skipOrgs: true,
        });
      }
    }
  }, [currentOrg?.id, location.pathname, slug, refreshData]);

  // Update share modal plan if plans change
  useEffect(() => {
    if (shareModalPlan) {
      const updated = plans.find((p) => p.id === shareModalPlan.id);
      if (updated) setShareModalPlan(updated);
    }
  }, [plans]);

  const onOrgCreated = async (newOrg: Organisation) => {
    const freshOrgs = await refreshOrgs();
    const created = freshOrgs.find((o) => o.id === newOrg.id) || newOrg;
    switchOrg(created.slug);
    navigate(`/${created.slug}/dashboard`);
  };

  const handleSwitchOrg = (org: Organisation) => {
    navigate(`/${org.slug}/dashboard`);
  };

  const handleCreatePlan = () => {
    if (!currentOrg) return;

    // Pre-emptively check limit for FREE tier
    if (currentOrg.subscriptionStatus === "FREE" && plans.length >= 5) {
      setLimitReachedMessage(
        "Free workspaces are limited to 5 plans. Upgrade to Team to create unlimited plans and collaborate with your whole team.",
      );
      return;
    }

    navigate(`/${currentOrg.slug}/plans/new`);
  };

  const handleEditPlan = (id: string) => {
    if (currentOrg) navigate(`/${currentOrg.slug}/plans/${id}/edit`);
  };

  const handleDeletePlan = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeleteConfirmPlanId(id);
  };

  const handleDeleteRun = async (id: string) => {
    try {
      const token = await getToken();
      await deleteRun(id, token || undefined);
      refreshData();
    } catch (e) {
      setErrorMessage("Failed to delete run: " + e);
    }
  };

  const confirmDeletePlan = async () => {
    if (!deleteConfirmPlanId) return;
    try {
      const token = await getToken();
      await deletePlan(deleteConfirmPlanId, token || undefined);
      setDeleteConfirmPlanId(null);
      refreshData();
    } catch (e) {
      setErrorMessage(String(e));
    }
  };

  const openRunModal = (e: React.MouseEvent, plan: TaskPlan) => {
    e.stopPropagation();
    setRunModalPlan(plan);
  };

  const openShareModal = (e: React.MouseEvent, plan: TaskPlan) => {
    e.stopPropagation();
    setShareModalPlan(plan);
  };

  const confirmRunPlan = async (name: string, schedule: string) => {
    if (!runModalPlan || !currentOrg) return;

    let scheduledTime: number | undefined = undefined;
    if (schedule) {
      const date = new Date(schedule);
      if (!isNaN(date.getTime()) && date.getTime() > Date.now()) {
        scheduledTime = date.getTime();
      }
    }

    try {
      const token = await getToken();
      const run = await startRun(
        runModalPlan.id,
        currentOrg.id,
        token || undefined,
        name || runModalPlan.name,
        scheduledTime,
      );

      setRunModalPlan(null);
      navigate(`/${currentOrg.slug}/runs/${run.id}`);
    } catch (e: any) {
      if (e.message?.includes("limit") || e.message?.includes("per month")) {
        setLimitReachedMessage(e.message);
      } else {
        setErrorMessage("Failed to start run: " + e);
      }
    }
  };

  const handleResumeRun = (runId: string) => {
    if (currentOrg) navigate(`/${currentOrg.slug}/runs/${runId}`);
  };

  if (!user || isOrgsLoading || (slug && !currentOrg)) {
    // Loading state while org is determined
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center text-slate-400 gap-4">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const modals = (
    <>
      <CreateOrgModal
        isOpen={createOrgModalOpen}
        onClose={() => setCreateOrgModalOpen(false)}
        onOrgCreated={onOrgCreated}
      />
      {runModalPlan && (
        <RunModal
          plan={runModalPlan}
          onClose={() => setRunModalPlan(null)}
          onConfirm={confirmRunPlan}
        />
      )}
      {shareModalPlan && currentOrg && (
        <ShareModal
          plan={shareModalPlan}
          currentOrg={currentOrg}
          onClose={() => setShareModalPlan(null)}
          onUpdate={refreshData}
        />
      )}
      <ConfirmationModal
        isOpen={!!deleteConfirmPlanId}
        onClose={() => setDeleteConfirmPlanId(null)}
        onConfirm={confirmDeletePlan}
        title="Delete Plan"
        message="Are you sure you want to delete this task plan? This cannot be undone."
        confirmText="Delete Plan"
        confirmVariant="danger"
      />
      <ErrorModal
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        message={errorMessage || ""}
      />
      {currentOrg && (
        <LimitReachedModal
          isOpen={!!limitReachedMessage}
          onClose={() => setLimitReachedMessage(null)}
          message={limitReachedMessage || ""}
          onUpgrade={() => navigate(`/${currentOrg.slug}/workspace/billing`)}
        />
      )}
    </>
  );

  return (
    <AppShell
      mobileMenuOpen={mobileMenuOpen}
      setMobileMenuOpen={setMobileMenuOpen}
      currentOrg={currentOrg}
      organisations={organisations}
      onSwitchOrg={handleSwitchOrg}
      onOrgCreated={onOrgCreated}
      tpUser={tpUser!}
      modals={modals}
      onOpenCreateOrg={() => setCreateOrgModalOpen(true)}
    >
      <Routes>
        <Route
          path="dashboard"
          element={
            <Dashboard
              plans={plans}
              runs={runs}
              currentUser={tpUser!}
              currentOrg={currentOrg}
              isLoading={isDataLoading}
              onCreatePlan={handleCreatePlan}
              onEditPlan={handleEditPlan}
              onDeletePlan={handleDeletePlan}
              onDeleteRun={handleDeleteRun}
              onRunPlan={openRunModal}
              onSharePlan={openShareModal}
              onResumeRun={handleResumeRun}
              onRefresh={refreshData}
            />
          }
        />
        <Route
          path="workspace/:tabId?"
          element={
            <TeamManager
              currentUser={tpUser!}
              currentOrg={currentOrg}
              onOpenCreateWorkspace={() => setCreateOrgModalOpen(true)}
            />
          }
        />
        <Route
          path="plans/new"
          element={
            <PlanEditor
              currentOrg={currentOrg}
              onClose={() => {
                navigate(`/${currentOrg.slug}/dashboard`);
              }}
            />
          }
        />
        <Route
          path="plans/:planId/edit"
          element={
            <PlanEditor
              currentOrg={currentOrg}
              onClose={() => {
                navigate(`/${currentOrg.slug}/dashboard`);
              }}
            />
          }
        />
        <Route
          path="runs/:runId"
          element={
            <PlanRunner
              currentOrg={currentOrg}
              onClose={() => {
                navigate(`/${currentOrg.slug}/dashboard`);
              }}
            />
          }
        />
        <Route
          path="runs"
          element={
            <RunsList
              runs={runs}
              currentOrg={currentOrg}
              onDeleteRun={handleDeleteRun}
              onRefresh={refreshData}
            />
          }
        />
        <Route path="subscription/success" element={<SubscriptionSuccess />} />
        <Route path="settings" element={<UserSettings />} />
      </Routes>
    </AppShell>
  );
};

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoaded, isSignedIn } = useUser();
  const location = useLocation();

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center text-slate-400 gap-4">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Component to handle legacy redirects or direct access to /dashboard
const OrgResolver: React.FC = () => {
  const { organisations, isLoading } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;

    if (organisations.length > 0) {
      const lastVisitedOrgSlug = localStorage.getItem("lastVisitedOrgSlug");
      const targetOrg =
        organisations.find((o) => o.slug === lastVisitedOrgSlug) ||
        organisations[0];

      let targetPath = "dashboard";
      if (location.pathname.includes("/workspace/billing")) {
        targetPath = "workspace/billing";
      } else if (location.pathname.includes("/workspace")) {
        targetPath = "workspace";
      }

      navigate(`/${targetOrg.slug}/${targetPath}`, { replace: true });
    } else {
      // This case should be handled by WorkspaceProvider (it creates a default org)
      // But if it's still empty, just go to landing or wait
    }
  }, [organisations, isLoading, navigate, location.pathname]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center text-slate-400 gap-4">
      <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  const navigate = useNavigate();
  const { isSignedIn } = useUser();

  return (
    <Routes>
      <Route
        path="/"
        element={<Landing onEnterApp={() => navigate("/dashboard")} />}
      />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* Org Resolver for legacy/root paths */}
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <OrgResolver />
          </RequireAuth>
        }
      />
      <Route
        path="/workspace"
        element={
          <RequireAuth>
            <OrgResolver />
          </RequireAuth>
        }
      />
      <Route
        path="/workspace/billing"
        element={
          <RequireAuth>
            <OrgResolver />
          </RequireAuth>
        }
      />

      {/* New Org-scoped Routes */}
      <Route
        path="/:slug/*"
        element={
          <RequireAuth>
            <WorkspaceRoutes />
          </RequireAuth>
        }
      />

      <Route
        path="/github/callback"
        element={
          <RequireAuth>
            <GithubCallback />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <WorkspaceProvider>
        <NotificationsProvider>
          <AppRoutes />
        </NotificationsProvider>
      </WorkspaceProvider>
    </BrowserRouter>
  );
};

export default App;
